import { useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, CreditCard, ExternalLink, RefreshCw } from "lucide-react";
import { InfoHint } from "@/components/InfoHint";
import { HINTS } from "@/lib/credentialHints";
import api, { formatApiErrorDetail } from "@/lib/api";

function Field({ label, hint, hintTestid, ...props }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</Label>
        {hint && <InfoHint text={hint} testid={hintTestid} />}
      </div>
      <Input className="font-mono text-sm" {...props} />
    </div>
  );
}

function ActivateBtn({ sub, provider, onChange }) {
  const isActive = sub.active_provider === provider;
  const setActive = async () => {
    try {
      const { data } = await api.put(`/subaccounts/${sub.id}/provider`, { active_provider: provider });
      toast.success(`${provider} set as active provider`);
      onChange(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  if (isActive)
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-mono text-emerald-400">
        <CheckCircle2 className="h-4 w-4" /> ACTIVE
      </span>
    );
  return (
    <Button variant="outline" size="sm" data-testid={`activate-${provider}-btn`} onClick={setActive}>
      Set Active
    </Button>
  );
}

export function SquareForm({ sub, onChange }) {
  const [appId, setAppId] = useState(sub.square?.application_id || "");
  const [locId, setLocId] = useState(sub.square?.location_id || "");
  const [token, setToken] = useState("");
  const [env, setEnv] = useState(sub.square?.environment || "sandbox");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/subaccounts/${sub.id}/square`, {
        application_id: appId,
        location_id: locId,
        access_token: token,
        environment: env,
      });
      toast.success("Square credentials saved");
      setToken("");
      onChange(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const connectOAuth = async () => {
    try {
      const { data } = await api.get(`/square/oauth/start?sub_id=${sub.id}`);
      window.location.assign(data.authorization_url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const refreshToken = async () => {
    try {
      const { data } = await api.post(`/subaccounts/${sub.id}/square/refresh`);
      toast.success("Square token refreshed");
      onChange(data.subaccount);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const activeEnv = sub.square_env || "sandbox";
  const switchEnv = async (target) => {
    if (target === activeEnv) return;
    try {
      const { data } = await api.put(`/subaccounts/${sub.id}/square-env`, { environment: target });
      toast.success(`Switched to ${target}`);
      setEnv(target);
      onChange(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const EnvBtn = ({ value, configured }) => (
    <button
      type="button"
      data-testid={`square-env-${value}`}
      onClick={() => switchEnv(value)}
      className={`flex-1 rounded-md border px-3 py-1.5 text-xs font-mono uppercase tracking-wide transition-colors ${activeEnv === value ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/50" : "bg-secondary text-muted-foreground border-border hover:text-foreground"}`}
    >
      {value} {configured ? "●" : "○"}
    </button>
  );

  return (
    <Card className="p-5 border-emerald-500/20 space-y-4">
      <div className="rounded-md border border-border/70 p-2 space-y-1.5" data-testid="square-env-switch">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Environment · editing &amp; viewing</div>
        <div className="flex gap-2">
          <EnvBtn value="sandbox" configured={sub.square_sandbox_configured} />
          <EnvBtn value="production" configured={sub.square_production_configured} />
        </div>
        <div className="text-[10px] font-mono text-muted-foreground">● configured · ○ empty — transactions below are scoped to this environment</div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-emerald-400" />
          <h3 className="font-heading font-semibold">Square</h3>
          {sub.square?.configured && <span className="text-[10px] font-mono text-emerald-400">CONFIGURED</span>}
        </div>
        {sub.square?.configured && <ActivateBtn sub={sub} provider="square" onChange={onChange} />}
      </div>

      <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2">
        {sub.square?.connected_via === "oauth" ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-xs font-mono text-emerald-400">
              <CheckCircle2 className="h-4 w-4" />
              Connected via OAuth{sub.square?.merchant_id ? ` · ${sub.square.merchant_id}` : ""}
            </div>
            {sub.square?.token_expires_at && (
              <div className="text-[11px] font-mono text-muted-foreground">
                Token valid until {new Date(sub.square.token_expires_at).toLocaleDateString()} · auto-refreshes
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">One-click connect — authorize your Square account, no keys to paste.</p>
        )}
        <div className="flex gap-2">
          <Button type="button" data-testid="square-connect-oauth-btn" onClick={connectOAuth} className="flex-1 bg-emerald-600 hover:bg-emerald-500 gap-2">
            <ExternalLink className="h-4 w-4" />
            {sub.square?.connected_via === "oauth" ? "Reconnect" : "Connect with Square"}
          </Button>
          {sub.square?.connected_via === "oauth" && (
            <Button type="button" variant="outline" data-testid="square-refresh-token-btn" onClick={refreshToken} className="gap-2">
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
          )}
        </div>
      </div>

      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground text-center">or enter manually</div>

      <Field label="Application ID" hint={HINTS.square_app_id} hintTestid="square-appid-hint" data-testid="square-appid-input" value={appId} onChange={(e) => setAppId(e.target.value)} placeholder="sandbox-sq0idb-..." />
      <Field label="Location ID" hint={HINTS.square_location_id} hintTestid="square-locid-hint" data-testid="square-locid-input" value={locId} onChange={(e) => setLocId(e.target.value)} placeholder="L1234..." />
      <Field label="Access Token (secret)" hint={HINTS.square_access_token} hintTestid="square-token-hint" data-testid="square-token-input" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder={sub.square?.configured ? "•••••• (leave to re-enter)" : "EAAA..."} />
      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5">
          <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Environment</Label>
          <InfoHint text={HINTS.square_env} testid="square-env-hint" />
        </div>
        <Select value={env} onValueChange={setEnv}>
          <SelectTrigger data-testid="square-env-select" className="font-mono text-sm"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="sandbox">Sandbox</SelectItem>
            <SelectItem value="production">Production</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button data-testid="square-save-btn" onClick={save} disabled={busy || !appId || !locId || !token} className="w-full bg-emerald-600 hover:bg-emerald-500">
        {busy ? "Saving…" : "Save Square"}
      </Button>
    </Card>
  );
}

export function StripeForm({ sub, onChange }) {
  const [usePlatform, setUsePlatform] = useState(sub.stripe?.use_platform ?? true);
  const [secret, setSecret] = useState("");
  const [pub, setPub] = useState(sub.stripe?.publishable_key || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = usePlatform
        ? { use_platform: true }
        : { use_platform: false, secret_key: secret, publishable_key: pub };
      const { data } = await api.put(`/subaccounts/${sub.id}/stripe`, payload);
      toast.success("Stripe settings saved");
      setSecret("");
      onChange(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5 border-indigo-500/20 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-indigo-400" />
          <h3 className="font-heading font-semibold">Stripe</h3>
          {sub.stripe?.configured && <span className="text-[10px] font-mono text-indigo-400">CONFIGURED</span>}
        </div>
        {sub.stripe?.configured && <ActivateBtn sub={sub} provider="stripe" onChange={onChange} />}
      </div>

      <div className="flex items-center justify-between rounded-md border border-border p-3">
        <div>
          <div className="text-sm font-medium">Use built-in test key</div>
          <div className="text-xs text-muted-foreground">Platform Stripe sandbox — no account needed</div>
        </div>
        <Switch data-testid="stripe-platform-switch" checked={usePlatform} onCheckedChange={setUsePlatform} />
      </div>

      {!usePlatform && (
        <>
          <Field label="Secret Key" hint={HINTS.stripe_secret} hintTestid="stripe-secret-hint" data-testid="stripe-secret-input" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="sk_live_... / sk_test_..." />
          <Field label="Publishable Key" hint={HINTS.stripe_pub} hintTestid="stripe-pub-hint" data-testid="stripe-pub-input" value={pub} onChange={(e) => setPub(e.target.value)} placeholder="pk_..." />
        </>
      )}

      <Button data-testid="stripe-save-btn" onClick={save} disabled={busy || (!usePlatform && !secret)} className="w-full bg-indigo-600 hover:bg-indigo-500">
        {busy ? "Saving…" : "Save Stripe"}
      </Button>
    </Card>
  );
}

export default function ProviderConfig({ sub, onChange }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <SquareForm key={sub.square_env || "sandbox"} sub={sub} onChange={onChange} />
      <StripeForm sub={sub} onChange={onChange} />
    </div>
  );
}
